import { ResponsiveLine } from '@nivo/line';
import useNivoTheme from './useNivoTheme';
import ChartTooltip from './ChartTooltip';

/**
 * Line chart with two Y axes. The `primary` series uses the left axis as-is.
 * The `secondary` series is linearly rescaled into the primary axis range
 * (so both lines share the drawing area), and a custom right-axis layer
 * renders its real tick labels.
 *
 * Props:
 *   primary     = { id, color, unit, data: [{x, y}] }
 *   secondary   = { id, color, unit, data: [{x, y}] }
 *   leftLegend, rightLegend  (string)
 *   tickValues  (optional array for axisBottom)
 *   markers     (optional forwarded to ResponsiveLine)
 *   height      (px, default 350)
 */
export default function DualAxisLine({
    primary,
    secondary,
    leftLegend,
    rightLegend,
    tickValues,
    markers = [],
}) {
    const { theme, colors } = useNivoTheme();

    const pVals = primary.data.map(d => d.y);
    const sVals = secondary.data.map(d => d.y);

    const pMin = Math.min(...pVals, 0);
    const pMax = Math.max(...pVals);
    const sMin = Math.min(...sVals);
    const sMax = Math.max(...sVals);

    const span = pMax - pMin || 1;
    const sSpan = sMax - sMin || 1;
    const rescale = v => ((v - sMin) / sSpan) * span + pMin;
    const unscale = v => ((v - pMin) / span) * sSpan + sMin;

    const lineData = [
        { id: primary.id, data: primary.data },
        {
            id: secondary.id,
            data: secondary.data.map(d => ({ x: d.x, y: rescale(d.y) })),
        },
    ];

    const RightAxisLayer = ({ innerHeight, innerWidth }) => {
        const ticks = 5;
        const vals = Array.from({ length: ticks + 1 }, (_, i) =>
            sMin + (i * sSpan) / ticks
        );
        return (
            <g transform={`translate(${innerWidth}, 0)`}>
                <line
                    x1={0}
                    y1={0}
                    x2={0}
                    y2={innerHeight}
                    stroke={theme.axis.domain.line.stroke}
                />
                {vals.map((v, i) => {
                    const y = innerHeight - (i * innerHeight) / ticks;
                    return (
                        <g key={i} transform={`translate(0, ${y})`}>
                            <line
                                x1={0}
                                y1={0}
                                x2={5}
                                y2={0}
                                stroke={theme.axis.ticks.line.stroke}
                            />
                            <text
                                x={9}
                                y={0}
                                dy="0.32em"
                                fill={theme.axis.ticks.text.fill}
                                fontSize={theme.axis.ticks.text.fontSize}
                                fontFamily={theme.axis.ticks.text.fontFamily}
                            >
                                {v.toFixed(1) + (secondary.unit || '')}
                            </text>
                        </g>
                    );
                })}
                {rightLegend && (
                    <text
                        transform={`translate(50, ${innerHeight / 2}) rotate(90)`}
                        textAnchor="middle"
                        fill={theme.axis.legend.text.fill}
                        fontSize={theme.axis.legend.text.fontSize}
                    >
                        {rightLegend}
                    </text>
                )}
            </g>
        );
    };

    return (
        <ResponsiveLine
            data={lineData}
            margin={{ top: 30, right: 80, bottom: 60, left: 55 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: pMin, max: pMax }}
            curve="monotoneX"
            enablePoints={false}
            enableGridX={false}
            colors={[primary.color, secondary.color]}
            lineWidth={2}
            axisBottom={{ tickRotation: 45, tickValues }}
            axisLeft={{
                legend: leftLegend,
                legendPosition: 'middle',
                legendOffset: -45,
            }}
            axisRight={null}
            markers={markers}
            legends={[
                {
                    anchor: 'top',
                    direction: 'row',
                    translateY: -20,
                    itemWidth: 160,
                    itemHeight: 16,
                    symbolSize: 8,
                    itemTextColor: colors.secondary,
                },
            ]}
            layers={[
                'grid',
                'markers',
                'axes',
                'areas',
                'crosshair',
                'lines',
                'points',
                'slices',
                'mesh',
                'legends',
                RightAxisLayer,
            ]}
            enableSlices="x"
            sliceTooltip={({ slice }) => (
                <ChartTooltip
                    title={String(slice.points[0].data.x)}
                    rows={slice.points.map(p => {
                        const isSecondary = p.seriesId === secondary.id;
                        const realY = isSecondary ? unscale(p.data.y) : p.data.y;
                        const unit = isSecondary
                            ? secondary.unit || ''
                            : primary.unit || '';
                        return {
                            label: p.seriesId,
                            value: realY.toFixed(2) + unit,
                            color: p.seriesColor,
                        };
                    })}
                />
            )}
            theme={theme}
        />
    );
}
